// Hilos — the followed threads of the current team (shared/API.md §13; ios/Sources/Views/
// ThreadsView.swift). Local-first: rows render from the store on the first frame, one refresh rides
// the open, and switching tabs never touches the network. Opening a row marks it read on the tap.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { ThreadsSnapshot } from '../../../data/features/destinations';
import { ThreadsDerivation, type ThreadsTab } from '../../../core/threadsDerivation';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, DestinationFrame, EmptyState, InlineStatus, Segmented } from '../../kit/Kit';
import { useLive, useMinuteClock } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { Icon } from '../../../design/icons';
import { applyOptimisticRead, buildThreadRows, isThreadUnread, replyCountLabel, type ThreadRow } from './destinationsModel';
import { PlaceLabel } from './PlaceLabel';
import './destinations.css';

type RefreshState = 'loading' | 'idle' | 'failed';

export function ThreadsView({ focusRootId }: { focusRootId: string | null }) {
  const shell = useShell();
  const teamId = shell.team.id;
  const now = useMinuteClock();
  // UNREADS first (user direction 2026-08-31): the inbox opens on what needs attention.
  const [tab, setTab] = useState<ThreadsTab>('unreads');
  const [refresh, setRefresh] = useState<RefreshState>('loading');
  const [attempt, setAttempt] = useState(0);
  const { value: snapshot } = useLive(() => data.call('threadsSnapshot', teamId), ['threads', 'threadParticipants', 'posts', 'users', 'channels', 'channelParticipants', 'syncState'], [teamId]);

  // The optimistic read state, tied to the snapshot it was made against (see applyOptimisticRead).
  const [optimistic, setOptimistic] = useState<{ base: ThreadsSnapshot | undefined; all: boolean; ids: Set<string> }>({ base: undefined, all: false, ids: new Set() });

  useEffect(() => {
    let alive = true;
    setRefresh('loading');
    data.call('refreshThreads', teamId).then(
      (r) => alive && setRefresh(r.ok ? 'idle' : 'failed'),
      () => alive && setRefresh('failed'),
    );
    return () => {
      alive = false;
    };
  }, [teamId, attempt]);

  const allRows = useMemo(() => (snapshot ? buildThreadRows(snapshot, now) : []), [snapshot, now]);
  const rows = useMemo(
    () => (optimistic.base === snapshot ? applyOptimisticRead(allRows, optimistic) : allRows),
    [allRows, optimistic, snapshot],
  );
  const visible = ThreadsDerivation.visibleThreads(rows, tab) as ThreadRow[];
  const unreadCount = rows.filter(isThreadUnread).length;

  const markRead = useCallback(
    (id: string) => {
      setOptimistic((o) => {
        const ids = new Set(o.base === snapshot ? o.ids : []);
        ids.add(id);
        return { base: snapshot, all: o.base === snapshot && o.all, ids };
      });
      void data.call('markThreadRead', teamId, id);
    },
    [snapshot, teamId],
  );

  const open = (row: ThreadRow) => {
    if (isThreadUnread(row)) markRead(row.id);
    shell.openThread(row.id);
  };

  // /<team>/threads/<rootId>: open that thread beside the list on arrival — once per id, so a
  // workspace that answers openThread with the same URL cannot loop.
  const focused = useRef<string | null>(null);
  useEffect(() => {
    if (!focusRootId || focused.current === focusRootId) return;
    focused.current = focusRootId;
    const row = allRows.find((r) => r.id === focusRootId);
    if (row && isThreadUnread(row)) markRead(row.id);
    shell.openThread(focusRootId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRootId]);

  const markAll = () => {
    setOptimistic({ base: snapshot, all: true, ids: new Set() });
    void data.call('markAllThreadsRead', teamId);
  };

  // Older pages: "Todos" only — unreads are complete by construction.
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const oldestId = visible.length ? visible[visible.length - 1].id : null;
  const canPage = tab === 'following' && hasMoreOlder && !!oldestId && refresh !== 'loading' && allRows.length >= 60;
  useEffect(() => {
    setHasMoreOlder(true);
  }, [teamId]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canPage || loadingOlder) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting) || !oldestId) return;
      observer.disconnect();
      setLoadingOlder(true);
      data.call('loadOlderThreads', teamId, oldestId).then(
        (more) => {
          setHasMoreOlder(more);
          setLoadingOlder(false);
        },
        () => setLoadingOlder(false),
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [canPage, loadingOlder, oldestId, teamId]);

  const unavailable = snapshot?.available === false;
  const neverFetched = !snapshot || snapshot.fetchedAt === 0;

  let body;
  if (unavailable) {
    body = <EmptyState title={t('web.destinations.threads.unavailable.title')} body={t('web.destinations.threads.unavailable.body')} />;
  } else if (!visible.length) {
    if (neverFetched && refresh === 'loading') body = <InlineStatus kind="loading" text={t('web.destinations.loading')} />;
    else if (neverFetched && refresh === 'failed') body = <InlineStatus kind="error" text={t('web.destinations.threads.refreshFailed')} onRetry={() => setAttempt((n) => n + 1)} />;
    else body = <EmptyState title={tab === 'unreads' ? t('threads.empty.unreads') : t('threads.empty')} />;
  } else {
    body = (
      <>
        {refresh === 'failed' ? <InlineStatus kind="error" text={t('web.destinations.threads.refreshFailed')} onRetry={() => setAttempt((n) => n + 1)} /> : null}
        <div className="dest-list" role="list">
          {visible.map((row) => (
            <ThreadRowView key={row.id} row={row} active={row.id === focusRootId} onOpen={() => open(row)} />
          ))}
        </div>
        {canPage ? <div ref={sentinel} className="dest-sentinel" /> : null}
        {loadingOlder ? <InlineStatus kind="loading" text={t('web.destinations.threads.loadingOlder')} /> : null}
      </>
    );
  }

  return (
    <DestinationFrame
      icon="threads"
      title={t('threads.title')}
      subtitle={shell.team.displayName}
      actions={
        tab === 'unreads' && unreadCount > 0 && !unavailable ? (
          <span className="dest-action" title={t('threads.markAllRead')}>
            <Button kind="quiet" icon="checkDouble" onClick={markAll}>
              <span className="dest-action-label">{t('threads.markAllRead')}</span>
            </Button>
          </span>
        ) : null
      }
      toolbar={
        unavailable ? null : (
          <Segmented<ThreadsTab>
            value={tab}
            onChange={setTab}
            options={[
              { value: 'following', label: t('threads.tab.all') },
              { value: 'unreads', label: t('threads.tab.unreads'), count: unreadCount },
            ]}
          />
        )
      }
    >
      {body}
    </DestinationFrame>
  );
}

function ThreadRowView({ row, active, onOpen }: { row: ThreadRow; active: boolean; onOpen(): void }) {
  const unread = isThreadUnread(row);
  return (
    <div
      role="listitem"
      tabIndex={0}
      className={`dest-row thread-row${unread ? ' unread' : ''}${active ? ' active' : ''}`}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? (e.preventDefault(), onOpen()) : undefined)}
    >
      <div className="thread-faces" aria-hidden="true">
        {row.avatarUserIds.map((id) => (
          <Avatar key={id} userId={id} name={row.avatarNames[id] ?? '?'} size={28} version={row.avatarVersions[id] ?? 0} />
        ))}
      </div>
      <div className="dest-row-main">
        <div className="dest-row-head">
          <PlaceLabel name={row.channelName} type={row.placeType} />
          {row.isUrgent ? <span className="priority-pill urgent">{t('channel.priority.urgent')}</span> : null}
          <span className="dest-time">{row.timeLabel}</span>
        </div>
        <div className="thread-root">
          <strong>{row.rootAuthorName}</strong>
          <span>{row.rootText}</span>
        </div>
        {row.replyPreview ? <div className="thread-reply">{row.replyPreview}</div> : null}
        <div className="thread-meta">
          <span className="thread-count">
            <Icon name="reply" size={12} />
            {replyCountLabel(row.replyCount)}
          </span>
          <span className="thread-people">{row.participantSummary}</span>
          <span className="thread-state">
            {row.unreadMentions > 0 ? (
              <span className="badge">{row.unreadMentions}</span>
            ) : null}
            {row.unreadReplies > 0 ? <span className="thread-new">{tf('threads.newReplies', row.unreadReplies)}</span> : unread ? <span className="unread-dot-row" /> : null}
          </span>
        </div>
      </div>
    </div>
  );
}
